import { useState } from 'react'

import type { Attempt, Exam } from '../../api/exams'
import { asClock, signalInWords } from '../../api/exams'
import { Button } from '../../ui/Control'
import { Question } from './Question'

/**
 * One test, open: what it asks, and every sitting of it.
 *
 * The tasks are shown as they will be sat — the pages themselves, the written question drawn — rather
 * than as a list of their names. Somebody opening a test is checking what is in it, and a list of
 * headings would be the file-name view this product exists not to be.
 *
 * **A sitting's row says how it went, not merely that it happened.** Its state is threefold — still
 * open, handed in, judged — because "judged" is the one thing somebody wants to know about a sitting
 * they have behind them, and a judged sitting is obviously handed in. Where there is a judgement, the
 * mark stands beside its scale with the traffic light named in words; where there is not, the row
 * says how far the sitting got and how long it took, which is what tells three open sittings apart
 * when three dates do not.
 *
 * Nothing here is compared, averaged or ordered by mark. A grade book is a different product.
 *
 * **Deleting a test asks first, and says how much will go**: its tasks, its sittings and whatever was
 * written in them. Deleting one *sitting* does not ask — that is one attempt, and the paper it was
 * sat from is untouched. The rule is the design guideline's: ask only where it cannot be undone, and
 * then say how much.
 */
export interface OpenExamProps {
  readonly exam: Exam
  readonly attempts: readonly Attempt[]
  readonly onSit: () => void
  readonly onEdit: () => void
  readonly onOpenAttempt: (id: string) => void
  readonly onRemoveAttempt: (id: string) => void
  readonly onRemove: () => void
  readonly onClose: () => void
}

/**
 * What a sitting is, in one word.
 *
 * Three states and not two. "Judged" replaces "handed in" rather than standing beside it: a judged
 * sitting is obviously handed in, and saying both would be an answer to a question nobody asked.
 */
function stateOf(attempt: Attempt): string {
  if (attempt.assessment !== null) {
    return 'Judged'
  }
  return attempt.handedInAt === null ? 'Still open' : 'Handed in'
}

/** How far a sitting got. A task counts as answered once anything at all stands in it. */
function answeredOf(attempt: Attempt, tasks: number): string {
  const written = new Set<string>()
  attempt.answers.forEach((one) => {
    if (one.text.trim() !== '') {
      written.add(one.taskId)
    }
  })
  attempt.cardAnswers.forEach((one) => {
    if (one.text.trim() !== '') {
      written.add(one.taskId)
    }
  })
  return `${String(written.size)} of ${String(tasks)} answered`
}

export function OpenExam({
  exam,
  attempts,
  onSit,
  onEdit,
  onOpenAttempt,
  onRemoveAttempt,
  onRemove,
  onClose,
}: OpenExamProps) {
  const [asking, setAsking] = useState(false)

  return (
    <div className="open-exam">
      <header className="open-exam__head">
        <h1 className="open-exam__title">{exam.title}</h1>
        <p className="open-exam__about">
          {exam.tasks.length === 1 ? '1 task' : `${String(exam.tasks.length)} tasks`}
          {exam.subjectName !== null && ` · ${exam.subjectName}`}
        </p>
        <div className="controls">
          <Button tone="primary" onClick={onSit}>
            Take this test
          </Button>
          <Button onClick={onEdit}>Change it</Button>
          <Button
            tone="quiet"
            onClick={() => {
              setAsking(true)
            }}
          >
            Delete
          </Button>
          <Button onClick={onClose}>Close</Button>
        </div>
      </header>

      {asking && (
        <div className="open-exam__asking" role="alertdialog" aria-label="Delete this test?">
          <p>
            The test, its{' '}
            {exam.tasks.length === 1 ? 'one task' : `${String(exam.tasks.length)} tasks`} and{' '}
            {attempts.length === 0
              ? 'no sittings'
              : attempts.length === 1
                ? 'one sitting with what was written in it'
                : `${String(attempts.length)} sittings with everything written in them`}{' '}
            go for good.
          </p>
          <div className="controls">
            <Button tone="quiet" onClick={onRemove}>
              Delete it all
            </Button>
            <Button
              onClick={() => {
                setAsking(false)
              }}
            >
              Keep it
            </Button>
          </div>
        </div>
      )}

      <section className="open-exam__sittings" aria-label="The sittings of this test">
        <h2 className="open-exam__heading">Sittings</h2>
        {attempts.length === 0 ? (
          <p className="open-exam__none">Not sat yet.</p>
        ) : (
          <ul className="open-exam__list">
            {attempts.map((attempt) => (
              <li className="sitting-row" key={attempt.id}>
                <button
                  className="sitting-row__open"
                  type="button"
                  onClick={() => {
                    onOpenAttempt(attempt.id)
                  }}
                >
                  <span className="sitting-row__about">
                    <span className="sitting-row__when">
                      {new Date(attempt.startedAt).toLocaleString()}
                    </span>
                    <span className="sitting-row__state">
                      {stateOf(attempt)} · {asClock(attempt.elapsedMs)} ·{' '}
                      {answeredOf(attempt, exam.tasks.length)}
                    </span>
                  </span>
                  {attempt.assessment !== null && (
                    <span className="sitting-row__mark">
                      <span className="sitting-row__grade">{attempt.assessment.grade}</span>
                      <span className="sitting-row__scale">on {attempt.assessment.scale}</span>
                      {/* The colour never stands alone — here as everywhere in MyLib. */}
                      <span
                        className="sitting-row__signal"
                        data-signal={attempt.assessment.signal.toLowerCase()}
                      >
                        {signalInWords(attempt.assessment.signal)}
                      </span>
                    </span>
                  )}
                </button>
                <Button
                  icon="close"
                  iconOnlyLabel="Delete this sitting"
                  onClick={() => {
                    onRemoveAttempt(attempt.id)
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="open-exam__tasks" aria-label="What this test asks">
        <h2 className="open-exam__heading">What it asks</h2>
        <ol className="open-exam__questions">
          {exam.tasks.map((task, index) => (
            <li className="open-exam__question" key={task.id}>
              <h3 className="open-exam__task">Task {index + 1}</h3>
              <p className="open-exam__standard">Measured by: {task.schemeName}</p>
              <Question prompt={task.prompt} label={`The question of task ${String(index + 1)}`} />
            </li>
          ))}
        </ol>
      </section>
    </div>
  )
}
